import {
  BASE_SIGN_MESSAGE,
  isTokenExpired,
  isTokenMatchingAddress,
} from "@/lib/utils"
import { useCallback, useMemo } from "react"
import { useChainId, useConnection, useSignMessage } from "wagmi"
import { useGetNonce, useRequestAccessToken } from "@/hooks/auth"

import { toast } from "sonner"
import { useAppKit } from "@reown/appkit/react"

interface UseAuthVerificationProps {
  isVerified: boolean
  setIsVerified: (verified: boolean) => void
  updateAccessToken: (token?: string) => void
  accessToken?: string
}

export const useAuthVerification = ({
  isVerified,
  setIsVerified,
  updateAccessToken,
  accessToken,
}: UseAuthVerificationProps) => {
  const { address } = useConnection()
  const chainId = useChainId()
  const { signMessageAsync } = useSignMessage()
  const { mutateAsync: requestAccessToken } = useRequestAccessToken()
  const { close } = useAppKit()

  const { data: nonce } = useGetNonce({
    address,
    enabled: !isVerified && !!address,
  })

  // Check if token matches current address (prevent using token from different address)
  const isTokenValid = useMemo(() => {
    if (!accessToken || !address) return false
    if (isTokenExpired(accessToken)) return false
    return isTokenMatchingAddress(accessToken, address)
  }, [accessToken, address])

  const signVerifyMessage = useCallback(
    async (message: string) => {
      if (!isVerified && address && chainId) {
        try {
          const signature = await signMessageAsync({ message })
          return signature
        } catch (error: any) {
          const errorMessage =
            error?.shortMessage ?? error?.message ?? "Failed to sign message"
          toast.error("Error", {
            description: errorMessage,
          })
          return ""
        }
      }

      return ""
    },
    [address, chainId, isVerified, signMessageAsync]
  )

  const verifyAccount = useCallback(async () => {
    if (!address || !chainId || !nonce) {
      throw new Error("Missing required data for verification")
    }

    const message = BASE_SIGN_MESSAGE + nonce?.nonce
    const signature = await signVerifyMessage(message)

    if (!signature) {
      throw new Error("Signature required for verification")
    }

    const params = {
      wallet_address: address,
      message,
      signature,
      service_name: "gas-sponsor",
    }
    const res = await requestAccessToken(params)
    updateAccessToken(res?.data?.token)
    setIsVerified(true)
    close()
  }, [
    address,
    chainId,
    signVerifyMessage,
    nonce,
    requestAccessToken,
    updateAccessToken,
    setIsVerified,
    close,
  ])

  return {
    verifyAccount,
    signVerifyMessage,
    isTokenValid,
    setIsVerified,
    nonce,
  }
}
